import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AuthStore } from '@huddle-sports-platform/app-core';

import { AccountStore } from './account-store';
import { ClubBrandStore } from './club-brand';

/**
 * Does this login run this club?
 *
 * The server keeps an `adminOf` custom claim - an array of club ids where
 * the login holds `club:manage` - as a cache of the access doc that is the
 * real trust root. Every writer of that access doc syncs it and stamps
 * `accounts/{uid}.claimsSyncedAt`, which is how a client knows to go and
 * get a fresh token.
 *
 * Why this is app-local rather than the kit's `AuthStore.isAdmin`: that
 * signal collapses the whole token to ONE boolean bound to a single claim
 * name (`super_admin` by default, HQ's gate), and it is computed once per
 * auth emission and never recomputed. Club admin is per-club and changes
 * mid-session - the moment somebody accepts an invitation - so it needs its
 * own read and its own refresh.
 *
 * The first read is IMPERATIVE (`whenSettled`), not effect-driven: guards
 * run before Angular flushes root effects, and a guard waiting on an effect
 * deadlocks the router. The effect only catches LATER changes.
 *
 * HONESTY: this is what the app SHOWS, never what it enforces. The claim is
 * a cache; Firestore rules read the access doc, and a tampered client that
 * flips this reaches nothing it could not already reach.
 */
@Injectable({ providedIn: 'root' })
export class ClubAdmin {
  private readonly auth = inject(AuthStore);
  private readonly account = inject(AccountStore);
  private readonly clubBrand = inject(ClubBrandStore);

  private readonly clubIds = signal<readonly string[]>([]);

  /** Admin of the club this build belongs to. False in a brandless dev
   *  build - there is no club to administer. */
  readonly isAdmin = computed(() => {
    const clubId = this.clubBrand.brand()?.clubId;
    return !!clubId && this.clubIds().includes(clubId);
  });

  /** Whose token the claim above came from. */
  private readFor: string | null | undefined;
  private inflight: Promise<void> | null = null;
  /** The stamp the last read answered - a later one means re-read. */
  private lastStamp: number | undefined;

  constructor() {
    // Later changes only: a grant or revoke bumps claimsSyncedAt, and the
    // token this client holds is then stale.
    effect(() => {
      const stamp = this.account.account()?.claimsSyncedAt;
      if (stamp === undefined || stamp === this.lastStamp) return;
      this.lastStamp = stamp;
      void this.refresh();
    });
  }

  /** Resolves once the claim has been read for whoever is signed in now. */
  whenSettled(): Promise<void> {
    const uid = this.auth.user()?.uid ?? null;
    if (this.readFor === uid && !this.inflight) return Promise.resolve();
    if (this.inflight) return this.inflight;
    this.inflight = this.read(uid, false).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /**
   * Re-read the claim, forcing a token refresh.
   *
   * The refresh matters: a claim minted a second ago is not in the token
   * this client is holding, so the page that just accepted an invitation
   * calls this before deciding where to send them.
   */
  async refresh(): Promise<void> {
    this.readFor = undefined;
    this.inflight = this.read(this.auth.user()?.uid ?? null, true).finally(() => {
      this.inflight = null;
    });
    await this.inflight;
  }

  private async read(uid: string | null, force: boolean): Promise<void> {
    const user = this.auth.user();
    if (!uid || !user) {
      this.clubIds.set([]);
      this.readFor = uid;
      return;
    }
    try {
      const token = await user.getIdTokenResult(force);
      const claim = token.claims['adminOf'];
      this.clubIds.set(Array.isArray(claim) ? (claim as string[]).map(String) : []);
    } catch {
      // Fail CLOSED: an unreadable token is not an admin. The screens this
      // gates are conveniences; the rules are the boundary.
      this.clubIds.set([]);
    } finally {
      this.readFor = uid;
    }
  }
}
